import * as Sentry from '@sentry/react';

/**
 * Initialize Sentry with configuration
 * Call this once at app startup in main.tsx
 */
export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const environment = import.meta.env.MODE || 'development';
  const tracesSampleRate = environment === 'production' ? 0.1 : 1.0;

  if (!dsn) {
    console.warn('Sentry DSN not configured. Set VITE_SENTRY_DSN in your .env file.');
    return;
  }

  try {
    Sentry.init({
      dsn,
      environment,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: true,
          blockAllMedia: true,
        }),
      ],
      // Performance Monitoring
      tracesSampleRate,
      // Session Replay
      replaysSessionSampleRate: environment === 'production' ? 0.1 : 1.0,
      replaysOnErrorSampleRate: 1.0,
      // Error Filtering
      beforeSend(event, hint) {
        // Filter out errors we don't want to track
        const error = hint.originalException;
        
        // Don't send errors from extensions
        if (error instanceof Error && error.message.includes('Extension context invalidated')) {
          return null;
        }
        
        // Don't send network errors that are handled gracefully
        if (event.exception) {
          const errorMessage = event.exception.values?.[0]?.value || '';
          if (errorMessage.includes('Network Error') && event.tags?.handled === true) {
            return null;
          }
        }
        
        return event;
      },
    });
  } catch (error) {
    console.error('Sentry initialization failed:', error);
  }
}

/**
 * Set user context for Sentry
 */
export function setSentryUser(userId: string | null, username?: string | null, email?: string | null) {
  if (userId) {
    Sentry.setUser({
      id: userId,
      username: username || undefined,
      email: email || undefined,
    });
  } else {
    Sentry.setUser(null);
  }
}

/**
 * Add breadcrumb for Sentry
 */
export function addBreadcrumb(message: string, category?: string, level: Sentry.SeverityLevel = 'info', data?: any) {
  Sentry.addBreadcrumb({
    message,
    category: category || 'custom',
    level,
    data,
    timestamp: Date.now() / 1000,
  });
}

/**
 * Capture exception with context
 */
export function captureException(error: Error, context?: {
  tags?: Record<string, string>;
  extra?: Record<string, any>;
  level?: Sentry.SeverityLevel;
}) {
  Sentry.withScope((scope) => {
    if (context?.tags) {
      Object.entries(context.tags).forEach(([key, value]) => {
        scope.setTag(key, value);
      });
    }
    
    if (context?.extra) {
      Object.entries(context.extra).forEach(([key, value]) => {
        scope.setExtra(key, value);
      });
    }
    
    if (context?.level) {
      scope.setLevel(context.level);
    }
    
    Sentry.captureException(error);
  });
}

/**
 * Capture message with context
 */
export function captureMessage(message: string, level: Sentry.SeverityLevel = 'info', context?: {
  tags?: Record<string, string>;
  extra?: Record<string, any>;
}) {
  Sentry.withScope((scope) => {
    if (context?.tags) {
      Object.entries(context.tags).forEach(([key, value]) => {
        scope.setTag(key, value);
      });
    }
    
    if (context?.extra) {
      Object.entries(context.extra).forEach(([key, value]) => {
        scope.setExtra(key, value);
      });
    }
    
    Sentry.captureMessage(message, level);
  });
}


